import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { catchError, forkJoin, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { FieldEditorService } from './field-editor/field-editor.service';
import { DocumentDynamicsService } from '../document-dynamics.service';
import { DacWorkflowBlocksService } from '../document-dynamics-blocks.service';
import { RefParamsService } from '../../ref-params/ref-params.service';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { DynaFields, DynaFieldsRequest, DynaFieldType, DynaStatus, RefTypeEnum, TemplateResponse } from '@/types';
import { DynamicForm } from '@/components/form-builder/form-builder.types';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { getDACFormObject } from './utils/document-dynamics-form-object';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PassingModeService } from '../../passing-mode/passing-mode.service';
import { SelectOption } from '@/components/form-builder/form-builder.types';
import { DynaDocsModelsService } from '../../dyna-docs-models/dyna-docs-models.service';
import { SectionVariablesListComponent } from './section-variables-list/section-variables-list.component';
import { TemplatePlaceholdersReviewComponent } from './template-placeholders-review/template-placeholders-review.component';

@Component({
  selector: 'sm-manage-document-dynamics',
  templateUrl: './manage-document-dynamics.component.html',
  styleUrls: ['./manage-document-dynamics.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    FormBuilderComponent,
    ButtonModule,
    BlockBuilderComponent,
    DrawerComponent,
    TranslateModule,
    SectionVariablesListComponent,
    TemplatePlaceholdersReviewComponent,
  ],
})
export class ManageDocumentDynamicsComponent implements OnInit, OnDestroy {
  private messageService = inject(MessageService);
  private dacWorkflowBlocksService = inject(DacWorkflowBlocksService);
  private documentDynamicsService = inject(DocumentDynamicsService);
  private translate = inject(TranslateService);

  readonly dacRepository = inject(DacRepository);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private refParamsService = inject(RefParamsService);
  private passingModeService = inject(PassingModeService);
  private dynaDocsModelsService = inject(DynaDocsModelsService);
  private fieldEditorService = inject(FieldEditorService);

  @ViewChild('fieldEditorDrawer') fieldEditorDrawerComponent!: DrawerComponent;
  @ViewChild('placeholdersReview') placeholdersReviewComponent?: TemplatePlaceholdersReviewComponent;
  @ViewChild('sectionVariablesList') sectionVariablesListComponent?: SectionVariablesListComponent;
  fieldEditorDrawerObject?: DrawerObject;

  private destroy$ = new Subject<void>();
  protected loading = false;
  protected isEditMode = false;
  protected showFieldEditor = false;
  protected selectedField: DynaFields | null = null;
  protected isNewField = true;
  protected MarketTypeTypesOptions: { name: string; code: number }[] = [];
  protected ModePassationOptions: SelectOption[] = [];
  protected DynaDocsModelOptions: SelectOption[] = [];
  title = 'dynamicDocument.form.create';

  protected breadcrumb = [
    { label: 'settings.refParams.breadcrumb.parametres', routerLink: ['/settings'] },
    { label: 'dynamicDocument.breadcrumb.create', routerLink: ['/settings', 'manage-document-dynamics'] },
  ];

  protected formObject: DynamicForm = getDACFormObject({
    store: this.dacRepository,
    marketType: this.MarketTypeTypesOptions,
    modePassationOptions: this.ModePassationOptions,
    dynaDocsModelOptions: this.DynaDocsModelOptions,
    onMarketTypeChange: this.onMarketTypeChange.bind(this),
    downloadTemplate: this.downloadTemplate.bind(this),
    uploadTemplate: this.uploadTemplate.bind(this),
    removeTemplate: this.removeTemplate.bind(this),
    downloadGuide: this.downloadGuide.bind(this),
    uploadGuide: this.uploadGuide.bind(this),
    removeGuide: this.removeGuide.bind(this),
    isEditMode: of(this.isEditMode),
  });

  protected controlBlocks: DynamicBlocks = this.dacWorkflowBlocksService.getPreDraftDacBlocks({
    save: this.saveDocument.bind(this),
    rest: this.resetForm.bind(this),
    history: () => console.log('History action triggered'),
  });

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.cleanupResources();
  }

  private initializeComponent(): void {
    this.resetForm();
    forkJoin([this.loadMarketType(), this.loadDynaDocsModels()]).subscribe({
      next: () => {
        const id = this.route.snapshot.params['id'];
        if (id) {
          this.title = 'dynamicDocument.update';
          this.handleEditMode(id);
        } else {
          this.initializeForm();
        }
      },
      error: this.handleLoadError.bind(this),
    });
  }

  private loadMarketType(): Observable<void> {
    return this.refParamsService.getSpecializedRefParams(RefTypeEnum.MARKET_TYPE).pipe(
      map(marketType => {
        this.MarketTypeTypesOptions = marketType.map(ref => ({
          name: ref.description,
          code: ref.id,
        }));
      }),
      takeUntil(this.destroy$)
    );
  }

  private loadDynaDocsModels(): Observable<void> {
    return this.dynaDocsModelsService.getAllActives().pipe(
      map(response => {
        this.DynaDocsModelOptions = response.map(model => ({
          name: model.description,
          code: model.dynaDocsModelId,
        }));
      }),
      catchError(() => {
        this.DynaDocsModelOptions = [];
        return of(undefined);
      }),
      takeUntil(this.destroy$)
    );
  }

  private onMarketTypeChange(typeMarcheId?: number) {
    if (!typeMarcheId) {
      this.ModePassationOptions = [];
      this.initializeForm();
      return;
    }
    this.passingModeService
      .getByMarketType(typeMarcheId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const list: any[] = Array.isArray(response) ? response : (response?.content ?? []);
          this.ModePassationOptions = list.map(mp => ({ name: mp.libelle, code: mp.id }));
          this.initializeForm();
        },
        error: () => {
          this.ModePassationOptions = [];
          this.initializeForm();
        },
      });
  }

  private handleEditMode(id: number): void {
    this.isEditMode = true;
    this.loadDocument(id)
      .then(() => {
        this.initializeForm();
        this.scanTemplateIfRequested();
      })
      .catch(this.handleLoadError.bind(this));
  }

  // On a brand-new document, the template is uploaded (and its **placeholders** would be
  // extracted) before the document has an id - isEditMode is still false at that point, so
  // uploadTemplate() intentionally skips extraction (see comment there) and the review
  // component isn't even in the DOM yet (*ngIf="isEditMode"). handleDocumentSaveSuccess()
  // flags this via router state when navigating into the edit-mode URL; pick it up here once
  // the document (and its templateFileName) has loaded, so the scan still happens exactly once.
  private scanTemplateIfRequested(): void {
    const state = history.state as { scanTemplate?: boolean };
    if (!state?.scanTemplate) return;
    history.replaceState({ ...history.state, scanTemplate: false }, '');

    const templateFileName = this.dacRepository.getDto()?.templateFileName;
    if (!templateFileName) return;

    // Defer to the next tick so the *ngIf="isEditMode" branch has rendered and the
    // @ViewChild reference to the review component is resolved.
    setTimeout(() => this.placeholdersReviewComponent?.runExtraction(templateFileName));
  }

  private async loadDocument(id: number): Promise<void> {
    this.loading = true;
    try {
      const documentData = await this.documentDynamicsService
        .getDynaDocsById(id)
        .pipe(
          switchMap(document => this.processDocumentFiles(document)),
          takeUntil(this.destroy$)
        )
        .toPromise();
      this.updateRepositoryWithDocument(documentData);
      // preload passing modes for the loaded market type
      const typeId = documentData?.document?.typeMarche?.id;
      if (typeId) {
        await this.passingModeService
          .getByMarketType(typeId)
          .pipe(takeUntil(this.destroy$))
          .toPromise()
          .then((res: any) => {
            const list: any[] = Array.isArray(res) ? res : (res?.content ?? []);
            this.ModePassationOptions = list.map(mp => ({ name: mp.libelle, code: mp.id }));
          });
      }
    } catch (error) {
      this.handleLoadError(error);
      throw error;
    } finally {
      this.loading = false;
    }
  }

  private processDocumentFiles(document: any): Observable<{
    document: any;
    templateMetadata: any;
    guideMetadata: any;
  }> {
    const template$ = document.templateFileName
      ? this.documentDynamicsService.getTemplate(document.templateFileName).pipe(
          catchError(error => {
            console.error('Failed to load template metadata', error);
            return of(null);
          })
        )
      : of(null);

    const guide$ = document.guideFileName
      ? this.documentDynamicsService.getTemplate(document.guideFileName).pipe(
          catchError(error => {
            console.error('Failed to load guide metadata', error);
            return of(null);
          })
        )
      : of(null);

    return forkJoin([template$, guide$]).pipe(
      map(([templateMetadata, guideMetadata]) => ({
        document,
        templateMetadata,
        guideMetadata,
      }))
    );
  }

  private updateRepositoryWithDocument({ document, templateMetadata, guideMetadata }: any): void {
    this.dacRepository.setDto({
      id: document.id,
      name: document.name,
      dynaDocOrder: document.dynaDocOrder,
      typeMarcheId: document.typeMarche.id,
      modePassationId: document?.modePassation?.id,
      status: document.status,
      templateFileName: document.templateFileName,
      guideFileName: document.guideFileName,
      fields: document.fields || [],
      isStatic: document.isStatic,
      isAAO: document.isAAO,
      dynaDocsModelId: document.dynaDocsModel?.dynaDocsModelId,
    });

    this.dacRepository.set('isStaticSelected', { code: document.isStatic ? 'true' : 'false', name: 'Statique' });

    this.dacRepository.set('typeMarcheIdSelected', {
      code: document.typeMarche.id,
      name: document.typeMarche.description,
    });

    if (document?.modePassation) {
      this.dacRepository.set('modePassationIdSelected', {
        code: document.modePassation.id,
        name: document.modePassation.libelle,
      });
    } else {
      this.dacRepository.set('modePassationIdSelected', { code: undefined, name: '' });
    }

    this.dacRepository.set('statusIdSelected', {
      code: document.status,
      name: document.status == 0 ? 'Active' : 'Inactive',
    });

    this.dacRepository.set('isAAOSelected', {
      code: document.isAAO ? 'true' : 'false',
      name: document.isAAO ? 'Oui' : 'Non',
    });

    if (document?.dynaDocsModel) {
      const code = document.dynaDocsModel.dynaDocsModelId;
      const name = document.dynaDocsModel.description;
      const opt = this.DynaDocsModelOptions.find(o => o.code === code);
      this.dacRepository.set('dynaDocsModelIdSelected', opt ?? { code, name });
    } else {
      this.dacRepository.set('dynaDocsModelIdSelected', { code: undefined, name: '' });
    }

    if (templateMetadata) {
      this.dacRepository.set('templateMetadata', templateMetadata);
    }
    if (guideMetadata) {
      this.dacRepository.set('guideMetadata', guideMetadata);
    }
  }

  private initializeForm(): void {
    this.formObject = getDACFormObject({
      store: this.dacRepository,
      marketType: this.MarketTypeTypesOptions,
      modePassationOptions: this.ModePassationOptions,
      dynaDocsModelOptions: this.DynaDocsModelOptions,
      onMarketTypeChange: this.onMarketTypeChange.bind(this),
      downloadTemplate: this.downloadTemplate.bind(this),
      uploadTemplate: this.uploadTemplate.bind(this),
      removeTemplate: this.removeTemplate.bind(this),
      downloadGuide: this.downloadGuide.bind(this),
      uploadGuide: this.uploadGuide.bind(this),
      removeGuide: this.removeGuide.bind(this),
      isEditMode: of(this.isEditMode),
    });
  }

  protected resetForm(): void {
    if (!this.isEditMode) {
      // Full reset for new document
      this.dacRepository.setDto({
        id: undefined,
        name: '',
        dynaDocOrder: undefined,
        typeMarcheId: undefined,
        modePassationId: undefined,
        status: undefined,
        templateFileName: '',
        guideFileName: '',
        fields: [],
        isAAO: false,
      });
      this.dacRepository.set('typeMarcheIdSelected', { code: undefined, name: '' });
      this.dacRepository.set('modePassationIdSelected', { code: undefined, name: '' });
      this.dacRepository.set('statusIdSelected', { code: undefined, name: '' });
      this.dacRepository.set('isAAOSelected', { code: 'false', name: 'Non' });
    } else {
      // Reload the original document data (like in ngOnInit)
      const id = this.route.snapshot.params['id'];
      if (id) {
        this.loadDocument(id)
          .then(() => this.initializeForm())
          .catch(this.handleLoadError.bind(this));
      }
    }
  }
  // Called from the merged fields/shared-variables list (sm-section-variables-list) for its
  // per-row edit action on local fields.
  protected onEditLocalField(field: DynaFields): void {
    this.openFieldEditor(field);
  }

  // Called from the merged list for its per-row delete action on local fields.
  protected onDeleteLocalField(fieldId: number): void {
    this.deleteField(fieldId);
  }

  protected openFieldEditor(field?: DynaFields): void {
    if (!this.isEditMode) {
      this.showDocumentSaveRequiredError();
      return;
    }

    this.selectedField = field ? { ...field } : null;
    this.isNewField = !field;

    this.fieldEditorService.initializeFromField(this.selectedField, this.isNewField);
    this.fieldEditorDrawerObject = this.fieldEditorService.buildDrawerObject(
      () => this.onFieldSavedFromDrawer(),
      () => this.onFieldEditorCancel()
    );
    this.showFieldEditor = true;
  }

  private onFieldSavedFromDrawer(): void {
    const fieldData = this.fieldEditorService.getFormValue();
    if (!fieldData) return;
    this.onFieldSaved(fieldData);
  }

  protected onFieldSaved(fieldData: DynaFields): void {
    const currentDoc = this.dacRepository.getDto();
    if (!currentDoc?.id) {
      this.showDocumentSaveRequiredError();
      this.showFieldEditor = false;
      return;
    }

    this.saveField(fieldData, currentDoc.id);
  }

  private saveField(fieldData: DynaFields, docId: number): void {
    this.loading = true;

    const fieldRequest: DynaFieldsRequest = this.createFieldRequest(fieldData, docId);
    const fieldOperation = this.isNewField
      ? this.documentDynamicsService.createDynaFields(fieldRequest)
      : this.documentDynamicsService.updateDynaFields(fieldData.dynaFieldsId, fieldRequest);

    fieldOperation.pipe(takeUntil(this.destroy$)).subscribe({
      next: savedField => this.handleFieldSaveSuccess(savedField),
      error: () => this.handleFieldSaveError(),
    });
  }

  private createFieldRequest(fieldData: DynaFields, docId: number): DynaFieldsRequest {
    return {
      dynaDocsId: docId,
      name: fieldData.name,
      ref: fieldData.ref,
      label: fieldData.label,
      description: fieldData.description,
      status: fieldData.status,
      type: fieldData.type,
      source: fieldData.source,
      sourceField: fieldData.sourceField,
      dynaFieldsOrder: fieldData.dynaFieldsOrder,
      details: fieldData.details,
    };
  }

  private handleFieldSaveSuccess(savedField: DynaFields): void {
    if (this.isNewField) {
      this.dacRepository.addField(savedField);
    } else {
      this.dacRepository.updateField(savedField);
    }
    const message = this.translate.instant(this.isNewField ? 'dynamicDocument.createdField' : 'dynamicDocument.updatedField');
    this.showSuccessMessage(message);
    this.showFieldEditor = false;
    this.loading = false;
  }

  private handleFieldSaveError(): void {
    const message = this.translate.instant(this.isNewField ? 'dynamicDocument.createdField' : 'dynamicDocument.updatedField');
    this.showErrorMessage(message);
    this.loading = false;
  }

  protected deleteField(fieldId: number): void {
    const currentDoc = this.dacRepository.getDto();
    if (!currentDoc?.id) return;

    this.loading = true;
    this.documentDynamicsService
      .deleteDynaFields(fieldId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.handleFieldDeleteSuccess(fieldId),
        error: () => this.handleFieldDeleteError(),
      });
  }

  private handleFieldDeleteSuccess(fieldId: number): void {
    this.dacRepository.removeField(fieldId);
    const message = this.translate.instant('dynamicDocument.deletedField');
    this.showSuccessMessage(message);
    this.loading = false;
  }

  private handleFieldDeleteError(): void {
    const message = this.translate.instant('dynamicDocument.errors.deletedField');
    this.showErrorMessage(message);
    this.loading = false;
  }

  protected saveDocument(): void {
    const dto = this.dacRepository.getDto();
    if (!dto) {
      this.showErrorMessage(this.translate.instant('dynamicDocument.errors.dtoLoad'));
      return;
    }
    if (!this.validateDocument(dto)) return;

    this.loading = true;

    const templateFile = this.dacRepository.getPendingTemplateFile();
    const guideFile = this.dacRepository.getPendingGuideFile();
    const uploadChain = this.handleFileUploads(templateFile, guideFile);

    uploadChain
      .pipe(
        switchMap(([templateFileName, guideFileName]) => {
          const documentData = this.prepareDocumentData({
            ...dto,
            templateFileName: templateFileName || dto.templateFileName,
            guideFileName: guideFileName || dto.guideFileName,
          });

          const saveObservable = dto.id
            ? this.documentDynamicsService.updateDynaDoc(dto.id, documentData)
            : this.documentDynamicsService.createDynaDocs(documentData);

          return saveObservable;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: savedDoc => this.handleDocumentSaveSuccess(savedDoc, !!dto.id),
        error: err => this.handleDocumentSaveError(err.error.message),
      });
  }

  private handleFileUploads(templateFile: File | undefined, guideFile: File | undefined): Observable<[string | null, string | null]> {
    const uploads: Observable<string | null>[] = [];

    if (templateFile) {
      uploads.push(
        this.documentDynamicsService.uploadTemplate(templateFile).pipe(
          map(response => response.fileName),
          catchError(() => of(null))
        )
      );
    } else {
      uploads.push(of(null));
    }

    if (guideFile) {
      uploads.push(
        this.documentDynamicsService.uploadTemplate(guideFile).pipe(
          map(response => response.fileName),
          catchError(() => of(null))
        )
      );
    } else {
      uploads.push(of(null));
    }

    return forkJoin(uploads) as Observable<[string | null, string | null]>;
  }
  private validateDocument(dto: any): boolean {
    if (!dto?.name) {
      this.showErrorMessage(this.translate.instant('dynamicDocument.form.nameRequired'));
      return false;
    }

    if (!dto?.templateFileName && !dto?.isStatic) {
      this.showErrorMessage(this.translate.instant('dynamicDocument.form.templateRequired'));
      return false;
    }

    if (!dto.typeMarcheId) {
      this.showErrorMessage(this.translate.instant('dynamicDocument.form.categoryRequired'));
      return false;
    }

    if (!dto.modePassationId) {
      this.showErrorMessage(this.translate.instant('dynamicDocument.form.modePassationRequired'));
      return false;
    }
    return true;
  }

  private prepareDocumentData(dto: any): any {
    const currentDoc = this.dacRepository.getDto();
    return {
      id: dto.id,
      version: dto.version || 1,
      name: dto.name,
      templateFileName: dto.templateFileName,
      guideFileName: dto.guideFileName,
      typeMarcheId: dto.typeMarcheId,
      modePassationId: dto.modePassationId,
      dynaDocOrder: dto.dynaDocOrder || 0,
      status: dto.status || DynaStatus.NON_ACTIF,
      fields: currentDoc?.fields || [],
      isStatic: dto.isStatic,
      isAAO: dto.isAAO,
      dynaDocsModelId: dto.dynaDocsModelId,
    };
  }

  private handleDocumentSaveSuccess(savedDoc: any, isUpdate: boolean): void {
    this.showSuccessMessage(this.translate.instant('dynamicDocument.form.saved'));
    const currentFields = this.dacRepository.getDto()?.fields || [];
    const mergedDoc = {
      ...savedDoc,
      fields: savedDoc.fields || currentFields,
      typeMarcheId: savedDoc.typeMarche.id,
    };

    this.updateRepositoryAfterSave(mergedDoc);
    this.isEditMode = true;

    if (!isUpdate) {
      // Tell the next (edit-mode) instance of this component to run the placeholders scan
      // once it has loaded - see scanTemplateIfRequested(). The create/edit routes are
      // separate route entries, so navigating here recreates the component; a plain instance
      // field wouldn't survive that.
      const navigationExtras = savedDoc.templateFileName ? { state: { scanTemplate: true } } : undefined;
      this.router.navigate(['/settings/manage-document-dynamics', savedDoc.id], navigationExtras);
    }

    this.loading = false;
  }

  private updateRepositoryAfterSave(savedDoc: any): void {
    this.dacRepository.setDto({
      ...savedDoc,
      name: savedDoc.name || '',
      typeMarcheId: savedDoc.typeMarche.id,
      modePassationId: savedDoc.modePassation?.id,
      fields: savedDoc.fields || [],
      dynaDocsModelId: savedDoc.dynaDocsModel?.dynaDocsModelId,
    });

    this.dacRepository.set('typeMarcheIdSelected', {
      code: savedDoc.typeMarche.id,
      name: savedDoc.typeMarche.description,
    });

    if (savedDoc.modePassation) {
      this.dacRepository.set('modePassationIdSelected', {
        code: savedDoc.modePassation.id,
        name: savedDoc.modePassation.libelle,
      });
    }

    if (savedDoc.dynaDocsModel) {
      const code = savedDoc.dynaDocsModel.dynaDocsModelId;
      const name = savedDoc.dynaDocsModel.description;
      const opt = this.DynaDocsModelOptions.find(o => o.code === code);
      this.dacRepository.set('dynaDocsModelIdSelected', opt ?? { code, name });
    } else {
      this.dacRepository.set('dynaDocsModelIdSelected', { code: undefined, name: '' });
    }
  }

  private handleDocumentSaveError(err: any): void {
    this.showErrorMessage(err || this.translate.instant('dynamicDocument.errors.saved'));
    this.loading = false;
  }

  private downloadTemplate(): void {
    const templateFileName = this.dacRepository.getDto()?.templateFileName;
    if (!templateFileName) {
      this.showErrorMessage(this.translate.instant('dynamicDocument.errors.noModel'));
      return;
    }

    this.documentDynamicsService.downloadTemplate(templateFileName).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private uploadTemplate(file: File): Observable<TemplateResponse> {
    this.dacRepository.setPendingTemplateFile(file);

    return this.documentDynamicsService.uploadTemplate(file).pipe(
      tap({
        next: response => {
          this.dacRepository.setDto({
            ...this.dacRepository.getDto(),
            templateFileName: response.fileName,
          });
          this.dacRepository.set('templateMetadata', response);
          this.dacRepository.setPendingTemplateFile(undefined);
          this.showSuccessMessage(this.translate.instant('dynamicDocument.form.uploadModel'));
          // Only scan for **placeholders** once the document exists (has an id) - a brand
          // new, not-yet-saved document has nowhere to attach fields to yet.
          if (this.isEditMode) {
            this.placeholdersReviewComponent?.runExtraction(response.fileName);
          }
        },
        error: () => {
          this.showErrorMessage(this.translate.instant('dynamicDocument.errors.uploadModel'));
          this.dacRepository.setPendingTemplateFile(undefined);
          this.dacRepository.setDto({
            ...this.dacRepository.getDto(),
            templateFileName: '',
          });
          this.dacRepository.set('templateMetadata', {});
        },
      })
    );
  }

  private removeTemplate(): void {
    this.dacRepository.setDto({
      templateFileName: '',
      ...this.dacRepository.getDto(),
    });
    this.dacRepository.set('templateMetadata', {});
  }

  private downloadGuide(): void {
    const guideFileName = this.dacRepository.getDto()?.guideFileName;
    if (!guideFileName) {
      this.showErrorMessage(this.translate.instant('dynamicDocument.errors.noGuide'));
      return;
    }

    this.documentDynamicsService.downloadTemplate(guideFileName).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private uploadGuide(file: File): Observable<TemplateResponse> {
    this.dacRepository.setPendingGuideFile(file);

    return this.documentDynamicsService.uploadTemplate(file).pipe(
      tap({
        next: response => {
          this.dacRepository.setDto({
            ...this.dacRepository.getDto(),
            guideFileName: response.fileName,
          });
          this.dacRepository.set('guideMetadata', response);
          this.dacRepository.setPendingGuideFile(undefined);
          this.showSuccessMessage(this.translate.instant('dynamicDocument.form.uploadGuide'));
        },
        error: () => {
          this.showErrorMessage(this.translate.instant('dynamicDocument.errors.uploadGuide'));
          this.dacRepository.setPendingGuideFile(undefined);
          this.dacRepository.setDto({
            ...this.dacRepository.getDto(),
            guideFileName: '',
          });
          this.dacRepository.set('guideMetadata', {});
        },
      })
    );
  }

  private removeGuide(): void {
    this.dacRepository.setDto({
      guideFileName: '',
      ...this.dacRepository.getDto(),
    });
    this.dacRepository.set('guideMetadata', {});
  }

  private showDocumentSaveRequiredError(): void {
    this.showErrorMessage(this.translate.instant('dynamicDocument.errors.saveBeforeAddFields'));
  }

  private showSuccessMessage(detail: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Succès',
      detail,
    });
  }

  private showErrorMessage(detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Erreur',
      detail,
    });
  }

  private handleLoadError(error: any): void {
    this.showErrorMessage(error || this.translate.instant('dynamicDocument.errors.loading'));
    this.loading = false;
  }

  private cleanupResources(): void {
    this.dacRepository.reset();
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onFieldEditorCancel(): void {
    this.showFieldEditor = false;
    this.fieldEditorService.reset();
  }

  // Quick-create from the "search or create" control in the merged fields/shared-variables
  // list: the typed name matched no existing global variable, so create it as a local field
  // here - TEXT by default, to be refined afterward via the normal edit (pencil) action rather
  // than filling a full form up front. `order` is computed by the list component itself from
  // the current merged (local + shared) rows, so it lands right after everything already in
  // the section instead of colliding with a shared variable's position.
  protected onQuickCreateLocalField({ name, order }: { name: string; order: number }): void {
    const currentDoc = this.dacRepository.getDto();
    if (!currentDoc?.id) {
      this.showDocumentSaveRequiredError();
      return;
    }

    const fieldRequest: DynaFieldsRequest = {
      dynaDocsId: currentDoc.id,
      name,
      ref: '',
      label: name,
      description: '',
      status: DynaStatus.ACTIF,
      type: DynaFieldType.TEXT,
      source: '',
      sourceField: '',
      dynaFieldsOrder: order,
      details: [],
    };

    this.loading = true;
    this.documentDynamicsService
      .createDynaFields(fieldRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: savedField => {
          this.dacRepository.addField(savedField);
          this.showSuccessMessage(this.translate.instant('dynamicDocument.createdField'));
          this.loading = false;
        },
        error: () => {
          this.showErrorMessage(this.translate.instant('dynamicDocument.errors.saved'));
          this.loading = false;
        },
      });
  }

  // The placeholders review can both create local fields (already reflected via
  // dacRepository) and attach global variables (owned by the merged list's own state) -
  // refresh it so newly-attached shared variables show up immediately.
  protected onPlaceholdersCreated(): void {
    this.sectionVariablesListComponent?.refresh();
  }
}
